import { CircularProgress, Typography } from "@material-ui/core"
import { AddCircle } from "@material-ui/icons"
import { PropTypes } from "prop-types"
import React, { useContext } from "react"
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd"
import DarkButton from "~/components/Common/DarkButton"
import AppContext from "~/contexts/App"
import CategoryContext from "~/contexts/Category"
import AddCourseDialogGeneric from "~/dialogs/AddCourseDialogGeneric"
import AddCourseCard from "~/dialogs/AddCourseDialogGeneric/CourseCard"
import AddPathDialogGeneric from "~/dialogs/AddPathDialogGeneric"
import AddedPathCardGeneric from "~/dialogs/AddPathDialogGeneric/PathCard"

import PermissionsWrapper from "~/components/Common/PermissionsWrapper"
import Box from "../../Common/Box"
import useStyles from "./styles"

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

function CategoryCourse({ course, addedCourses, setAddedCourses, index }) {
  return (
    <Draggable draggableId={course?.id?.toString()} index={index}>
      {(provided) => {
        return (
          <AddCourseCard
            course={course}
            type="view"
            index={index}
            addedCourses={addedCourses}
            setAddedCourses={setAddedCourses}
            provided={provided}
          />
        )
      }}
    </Draggable>
  )
}

CategoryCourse.propTypes = {
  course: PropTypes.object,
  index: PropTypes.number,
  addedCourses: PropTypes.array,
  setAddedCourses: PropTypes.func,
}

const CategoryCourseList = React.memo(function CategoryCourseList({
  courses,
  setAddedCourses,
}) {
  return courses?.map((course, index) => (
    <CategoryCourse
      course={course}
      type="view"
      index={index}
      key={course.id}
      addedCourses={courses}
      setAddedCourses={setAddedCourses}
    />
  ))
})

CategoryCourseList.propTypes = {
  courses: PropTypes.array,
  setAddedCourses: PropTypes.func,
}

const DashboardLearningPath = ({ path, addedPaths, setAddedPaths, index }) => {
  return (
    <Draggable draggableId={path?.id?.toString()} index={index}>
      {(provided) => {
        return (
          <AddedPathCardGeneric
            path={path}
            type="view"
            index={index}
            addedPaths={addedPaths}
            setAddedPaths={setAddedPaths}
            provided={provided}
          />
        )
      }}
    </Draggable>
  )
}

DashboardLearningPath.propTypes = {
  path: PropTypes.object,
  index: PropTypes.number,
  addedPaths: PropTypes.array,
  setAddedPaths: PropTypes.func,
}
const DashboardLearningPathList = ({ paths, setAddedPaths }) => {
  return paths?.map((path, index) => (
    <DashboardLearningPath
      path={path}
      type="view"
      index={index}
      key={path.id}
      addedPaths={paths}
      setAddedPaths={setAddedPaths}
    />
  ))
}

DashboardLearningPathList.propTypes = {
  paths: PropTypes.array,
  setAddedPaths: PropTypes.func,
}

const CategoryContent = () => {
  const classes = useStyles()
  const { openDialog } = useContext(AppContext)

  const {
    categoryCourses,
    setCategoryCourses,
    categoryLearningPaths,
    setCategoryPaths,
    isLoadingLearningPaths,
    isLoadingCourses,
  } = useContext(CategoryContext)

  const renderCategoryCourses = () => {
    function onCategoryCoursesDragEnd(result) {
      if (!result.destination) {
        return
      }
      if (result.destination.index === result.source.index) {
        return
      }

      const orderedCategoryCourses = reorder(
        categoryCourses,
        result.source.index,
        result.destination.index,
      )
      setCategoryCourses(orderedCategoryCourses)
    }

    const addCategoryCourses = async () => {
      const newCourses = await openDialog(
        "addCourseDialogGeneric",
        categoryCourses,
      )
      if (newCourses !== null) {
        setCategoryCourses(newCourses)
      }
    }

    return (
      <div className="relative">
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="0"
          zIndex={10}
          width="100%"
          height="100%"
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              Category Courses
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          <PermissionsWrapper permissionGroup="admin">
            <DarkButton
              onClick={addCategoryCourses}
              // style={path?.published ? { display: "none" } : null}
              startIcon={
                <AddCircle
                  fontSize="small"
                  classes={{ root: classes.addCircle }}
                />
              }
            >
              Add Course
            </DarkButton>
          </PermissionsWrapper>
        </Box>
        <Box marginLeft={"37px"} marginRight="37px" height={"100%"}>
          {categoryCourses?.length > 0 && (
            <DragDropContext onDragEnd={onCategoryCoursesDragEnd}>
              <Droppable droppableId="course">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <CategoryCourseList
                      courses={categoryCourses}
                      setAddedCourses={setCategoryCourses}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
          {isLoadingCourses && (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
        <AddCourseDialogGeneric />
      </div>
    )
  }

  const renderDashboardLearningPaths = () => {
    function onDashboardLearningPathsDragEnd(result) {
      if (!result.destination) {
        return
      }

      if (result.destination.index === result.source.index) {
        return
      }

      const orderedDashboardLearningPaths = reorder(
        categoryLearningPaths,
        result.source.index,
        result.destination.index,
      )

      setCategoryPaths(orderedDashboardLearningPaths)
    }

    const addDashboardLearningPaths = async () => {
      const newLearningPaths = await openDialog(
        "addPathDialogGeneric",
        categoryLearningPaths,
      )
      if (newLearningPaths !== null) {
        setCategoryPaths(newLearningPaths)
      }
    }

    return (
      <div className="relative">
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          padding="31px 37px 8px 37px"
          marginBottom="10px"
          zIndex={10}
          width="100%"
        >
          <Box display="flex" flexDirection="row" flexWrap="wrap">
            <Typography
              variant="h3"
              color="primary"
              style={{ fontWeight: "700", marginRight: "8px" }}
            >
              Learning Paths
            </Typography>
            <Typography
              variant="h3"
              color="primary"
              className={classes.orderText}
            >
              In order as they appear
            </Typography>
          </Box>
          <PermissionsWrapper permissionGroup="admin">
            <DarkButton
              onClick={addDashboardLearningPaths}
              startIcon={
                <AddCircle
                  fontSize="small"
                  classes={{ root: classes.addCircle }}
                />
              }
            >
              Add Learning Path
            </DarkButton>
          </PermissionsWrapper>
        </Box>
        <Box marginLeft={"37px"} marginRight="37px" height={"100%"}>
          {categoryLearningPaths?.length > 0 && (
            <DragDropContext onDragEnd={onDashboardLearningPathsDragEnd}>
              <Droppable droppableId="path">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <DashboardLearningPathList
                      paths={categoryLearningPaths}
                      setAddedPaths={setCategoryPaths}
                    />
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
          {isLoadingLearningPaths && (
            <Box display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          )}
        </Box>
        <AddPathDialogGeneric />
      </div>
    )
  }

  return (
    <div className="mb-20">
      {renderCategoryCourses()}
      {renderDashboardLearningPaths()}
    </div>
  )
}

export default CategoryContent
